import { useEffect, useState } from "react";
import { Topbar, Sidebar, Loader, BreadCrump } from "../../components";
import { fetchRequests } from "../../Requests/getRequests";
import { useDispatch, useSelector } from "react-redux";
import { show } from '../../slices/toastSlice';
import { authStaff } from "../../utils/constants";
import { selectAccessToken } from "../../slices/staffSlice";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowLeft01Icon } from '@hugeicons/core-free-icons';

const NETWORK_OPTIONS = ['MTN', 'Vodafone', 'AirtelTigo'];
const GENDER_OPTIONS  = ['male', 'female'];
const EMPLOY_NATURE_OPTIONS = [
    { value: 'fullTime', text: 'Full Time' },
    { value: 'casual',   text: 'Casual'    },
];
const EDUCATION_OPTIONS = [
    'No Formal Education', 'Primary', 'JHS / Middle School', 'SHS / Technical',
    'HND', 'Diploma', 'Degree', 'Post Graduate',
];

function Field({ label, children, disabled }) {
    return (
        <div>
            <label className={`block text-xs font-medium mb-1.5 ${disabled ? 'text-gray-400 dark:text-gray-500' : 'text-gray-600 dark:text-gray-400'}`}>
                {label}
            </label>
            {children}
        </div>
    );
}

function ClientEdit() {
    const accessToken = useSelector(selectAccessToken);
    const dispatch    = useDispatch();
    const navigate    = useNavigate();
    const location    = useLocation();
    const clientId    = location.pathname.split("/")[3];

    const [client,    setClient]    = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [saving,    setSaving]    = useState(false);

    const [form, setForm] = useState({
        otherNames:           '',
        surname:              '',
        email:                '',
        gender:               '',
        phone:                '',
        network:              '',
        address:              '',
        dob:                  '',
        education:            '',
        occupation:           '',
        employNature:         '',
        digitalAddress:       '',
        nearestLandmark:      '',
        nextOfKin:            '',
        nextOfKinPhone:       '',
        nextOfKinAddress:     '',
        nextOfKinRelationship:'',
    });

    useEffect(() => {
        const load = async () => {
            const res = await fetchRequests(`/auth/staff/client/find`, "GET", authStaff(accessToken), { _id: clientId }, {});
            setIsLoading(false);

            if (res?.code === 401) {
                sessionStorage.clear();
                navigate("/login");
                dispatch(show({ type: "error", state: true, message: "Your session has expired. Please login again." }));
                return;
            }
            if (res?.err) {
                dispatch(show({ type: "error", state: true, message: res.err }));
                return;
            }

            setClient(res);
            setForm({
                otherNames:            res.otherNames            || '',
                surname:               res.surname               || '',
                email:                 res.email                 || '',
                gender:                res.gender                || '',
                phone:                 res.phone                 || '',
                network:               res.network               || '',
                address:               res.address               || '',
                dob:                   res.dob ? res.dob.split('T')[0] : '',
                education:             res.education             || '',
                occupation:            res.occupation            || '',
                employNature:          res.employNature          || '',
                digitalAddress:        res.digitalAddress        || '',
                nearestLandmark:       res.nearestLandmark       || '',
                nextOfKin:             res.nextOfKin             || '',
                nextOfKinPhone:        res.nextOfKinPhone        || '',
                nextOfKinAddress:      res.nextOfKinAddress      || '',
                nextOfKinRelationship: res.nextOfKinRelationship || '',
            });
        };

        load();
    }, [accessToken, clientId, dispatch, navigate]);

    const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        const res = await fetchRequests(`/auth/staff/client/update`, "PUT", authStaff(accessToken), {}, { _id: clientId, ...form });
        setSaving(false);

        if (res?.code === 401) {
            sessionStorage.clear();
            navigate("/login");
            dispatch(show({ type: "error", state: true, message: "Your session has expired. Please login again." }));
            return;
        }
        if (res?.err) {
            dispatch(show({ type: "error", state: true, message: res.err }));
            return;
        }

        dispatch(show({ type: "success", state: true, message: "Client details updated successfully." }));
        navigate(`/client/${clientId}`);
    };

    const inputCls = "w-full px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[#1e3a6e]/20 focus:border-[#1e3a6e] dark:focus:border-blue-500 transition-colors placeholder:text-gray-300 dark:placeholder:text-gray-500";
    const disabledCls = "w-full px-3 py-2 text-sm border border-gray-100 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed";

    const loanStateColor = (state) => {
        if (state === 'active')    return 'bg-emerald-100 text-emerald-700';
        if (state === 'completed') return 'bg-sky-100 text-sky-700';
        if (state === 'rejected')  return 'bg-red-100 text-red-700';
        return 'bg-slate-100 text-slate-700';
    };

    return (
        <div className='main-container'>
            <Topbar />
            <Sidebar />

            <main className='main-content pb-20'>
                <section className='section-container space-y-6'>

                    {isLoading && (
                        <div className='w-full py-12 flex justify-center'>
                            <Loader color="text-gray-500" size="w-6 h-6" />
                        </div>
                    )}

                    {!isLoading && client && (
                        <>
                        <div className="flex items-center justify-between">
                            <BreadCrump
                                path={`/client/${clientId}`}
                                main={`${client.otherNames} ${client.surname}`}
                                sub="Edit Details"
                            />
                            <Link
                                to={`/client/${clientId}`}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                            >
                                <HugeiconsIcon icon={ArrowLeft01Icon} size={14} />
                                Back
                            </Link>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-5">

                            {/* Personal Information */}
                            <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm p-6 space-y-5">
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50 border-b border-gray-100 dark:border-gray-700 pb-3">
                                    Personal Information
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field label="First / Other Names">
                                        <input type="text" className={inputCls} value={form.otherNames} onChange={set('otherNames')} required />
                                    </Field>
                                    <Field label="Surname">
                                        <input type="text" className={inputCls} value={form.surname} onChange={set('surname')} required />
                                    </Field>
                                    <Field label="Gender">
                                        <select className={inputCls} value={form.gender} onChange={set('gender')}>
                                            <option value="">-- select --</option>
                                            {GENDER_OPTIONS.map(g => (
                                                <option key={g} value={g} className="capitalize">{g.charAt(0).toUpperCase() + g.slice(1)}</option>
                                            ))}
                                        </select>
                                    </Field>
                                    <Field label="Date of Birth">
                                        <input type="date" className={inputCls} value={form.dob} onChange={set('dob')} />
                                    </Field>
                                    <Field label="Email">
                                        <input type="email" className={inputCls} value={form.email} onChange={set('email')} />
                                    </Field>
                                </div>
                            </div>

                            {/* Identity — read-only */}
                            <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm p-6 space-y-5">
                                <div className="border-b border-gray-100 dark:border-gray-700 pb-3 flex items-center gap-2">
                                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">Identity</h3>
                                    <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">Read only</span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field label="ID Type" disabled>
                                        <input type="text" className={disabledCls} value={client.idType || ''} readOnly disabled />
                                    </Field>
                                    <Field label="ID Number" disabled>
                                        <input type="text" className={disabledCls} value={client.idNumber || ''} readOnly disabled />
                                    </Field>
                                </div>
                            </div>

                            {/* Contact & Location */}
                            <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm p-6 space-y-5">
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50 border-b border-gray-100 dark:border-gray-700 pb-3">
                                    Contact & Location
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field label="Phone Number">
                                        <input type="text" className={inputCls} value={form.phone} onChange={set('phone')} maxLength={10} />
                                    </Field>
                                    <Field label="Phone Network">
                                        <select className={inputCls} value={form.network} onChange={set('network')}>
                                            <option value="">-- select --</option>
                                            {NETWORK_OPTIONS.map(n => (
                                                <option key={n} value={n}>{n}</option>
                                            ))}
                                        </select>
                                    </Field>
                                    <Field label="Address">
                                        <input type="text" className={inputCls} value={form.address} onChange={set('address')} />
                                    </Field>
                                    <Field label="Digital Address (GPS)">
                                        <input type="text" className={inputCls} value={form.digitalAddress} onChange={set('digitalAddress')} />
                                    </Field>
                                    <Field label="Nearest Landmark">
                                        <input type="text" className={inputCls} value={form.nearestLandmark} onChange={set('nearestLandmark')} />
                                    </Field>
                                </div>
                            </div>

                            {/* Employment */}
                            <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm p-6 space-y-5">
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50 border-b border-gray-100 dark:border-gray-700 pb-3">
                                    Employment & Education
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field label="Education">
                                        <select className={inputCls} value={form.education} onChange={set('education')}>
                                            <option value="">-- select --</option>
                                            {EDUCATION_OPTIONS.map(e => (
                                                <option key={e} value={e}>{e}</option>
                                            ))}
                                        </select>
                                    </Field>
                                    <Field label="Occupation">
                                        <input type="text" className={inputCls} value={form.occupation} onChange={set('occupation')} />
                                    </Field>
                                    <Field label="Employment Nature">
                                        <select className={inputCls} value={form.employNature} onChange={set('employNature')}>
                                            <option value="">-- select --</option>
                                            {EMPLOY_NATURE_OPTIONS.map(o => (
                                                <option key={o.value} value={o.value}>{o.text}</option>
                                            ))}
                                        </select>
                                    </Field>
                                </div>
                            </div>

                            {/* Next of Kin */}
                            <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm p-6 space-y-5">
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50 border-b border-gray-100 dark:border-gray-700 pb-3">
                                    Next of Kin
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <Field label="Full Name">
                                        <input type="text" className={inputCls} value={form.nextOfKin} onChange={set('nextOfKin')} />
                                    </Field>
                                    <Field label="Phone Number">
                                        <input type="text" className={inputCls} value={form.nextOfKinPhone} onChange={set('nextOfKinPhone')} maxLength={10} />
                                    </Field>
                                    <Field label="Address">
                                        <input type="text" className={inputCls} value={form.nextOfKinAddress} onChange={set('nextOfKinAddress')} />
                                    </Field>
                                    <Field label="Relationship">
                                        <input type="text" className={inputCls} value={form.nextOfKinRelationship} onChange={set('nextOfKinRelationship')} />
                                    </Field>
                                </div>
                            </div>

                            {/* Loans — read-only */}
                            <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm p-6 space-y-4">
                                <div className="border-b border-gray-100 dark:border-gray-700 pb-3 flex items-center gap-2">
                                    <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
                                        Loans ({client.loans?.length ?? 0})
                                    </h3>
                                    <span className="text-xs text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">Read only</span>
                                </div>
                                {client.loans?.length > 0 ? (
                                    <div className="flex flex-wrap gap-2">
                                        {client.loans.map((loan, i) => (
                                            <span
                                                key={i}
                                                className={`inline-flex items-center px-2.5 py-1.5 rounded-md text-xs font-medium ${loanStateColor(loan.state)}`}
                                            >
                                                {loan.type} — GHS {loan.amount?.toLocaleString()} — <span className="capitalize ml-1">{loan.state}</span>
                                            </span>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-400 dark:text-gray-500">No loans on record.</p>
                                )}
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-3 pb-4">
                                <button
                                    type="submit"
                                    disabled={saving}
                                    className="px-6 py-2.5 text-sm font-medium text-white bg-[#1e3a6e] hover:bg-[#264d91] rounded-lg transition-colors disabled:opacity-60"
                                >
                                    {saving ? 'Saving…' : 'Save Changes'}
                                </button>
                                <Link
                                    to={`/client/${clientId}`}
                                    className="px-6 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    Cancel
                                </Link>
                            </div>

                        </form>
                        </>
                    )}

                </section>
            </main>
        </div>
    );
}

export default ClientEdit;
